import * as React from "react";
import { ToggleOption } from "../components/MakeOfferPages/ToggleOptions";

interface MakeOfferContextData {
  getToggleOptions: (groupId?: string) => ToggleOption[] | null;
  setToggleOptions: (groupId: string, options: ToggleOption[]) => void;
  clearToggleOptions: (groupId?: string) => void;
}

const initialState: MakeOfferContextData = {
  getToggleOptions: () => null,
  setToggleOptions: () => {},
  clearToggleOptions: () => {},
};

const MakeOfferContext = React.createContext<MakeOfferContextData>(initialState);

export function MakeOfferProvider({ children }: { children: React.ReactNode }) {
  const [toggleOptionsByGroupId, setToggleOptionsByGroupId] = React.useState<Record<string, ToggleOption[]>>({});

  const getToggleOptions = React.useCallback((groupId?: string) => {
    if (!groupId) {
      return null;
    }

    return toggleOptionsByGroupId[groupId] ?? null;
  }, [toggleOptionsByGroupId]);

  const setToggleOptions = React.useCallback((groupId: string, options: ToggleOption[]) => {
    if (!groupId) {
      return;
    }

    setToggleOptionsByGroupId((previousState) => ({
      ...previousState,
      [groupId]: options,
    }));
  }, []);

  const clearToggleOptions = React.useCallback((groupId?: string) => {
    if (!groupId) {
      return;
    }

    setToggleOptionsByGroupId((previousState) => {
      if (!previousState[groupId]) {
        return previousState;
      }

      const nextState = { ...previousState };
      delete nextState[groupId];
      return nextState;
    });
  }, []);

  const value = React.useMemo(
    () => ({
      getToggleOptions,
      setToggleOptions,
      clearToggleOptions,
    }),
    [getToggleOptions, setToggleOptions, clearToggleOptions]
  );

  return (
    <MakeOfferContext.Provider value={value}>
      {children}
    </MakeOfferContext.Provider>
  );
}

export function useMakeOffer() {
  return React.useContext(MakeOfferContext);
}
